import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import { NetworkArea } from '../../../interfaces/services/areas';
import { ValueReferences } from '../../enums';
import { formatBalance } from '../../formattingUtils';
import { getGradeColor, getGradeStrokeColor } from '../../styleUtils';
import { FeatureTypes, MapSources } from '../enums';

/** Interface used for the properties object on the Balance Areas Map Source. */
export interface BalanceAreasMapSourceProperties {
    id: string;
    featureType: FeatureTypes;
    color: string;
    strokeColor: string;
    description: string;
}

/**
 * Loop through the areas and add a point feature to the source for each area location.
 * Adds the balance area source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param areas The balance areas to show on the map
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addBalanceAreasSource = (map: TrimbleMaps.Map, areas: NetworkArea[], theme: Theme): GeoJSONSourceRaw => {
    const features: GeoJSON.Feature<GeoJSON.Point, BalanceAreasMapSourceProperties>[] = [];

    areas.forEach((area) => {
        if (area.service_area_longitude_value && area.service_area_latitude_value) {
            const color = getGradeColor(area.average_daily_balance_grade, theme);

            const tooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${area.service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${area.service_area_id}</div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Balance Grade:</div>
                        <div class='mapTooltipCell' style='color: ${color};'>${area.average_daily_balance_grade || ValueReferences.NA}</div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Avg. Balance:</div>
                        <div class='mapTooltipCell'>${formatBalance(area.average_daily_balance_amount, 2, 2)}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [area.service_area_longitude_value, area.service_area_latitude_value],
                },
                properties: {
                    id: area.service_area_id,
                    featureType: FeatureTypes.AreaCenter,
                    color,
                    strokeColor: getGradeStrokeColor(area.average_daily_balance_grade, theme),
                    description: tooltipHtml,
                },
            });
        }
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features,
        },
    };

    if (map.getSource(MapSources.balanceAreasSource) === undefined) {
        map.addSource(MapSources.balanceAreasSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.balanceAreasSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
